/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../Auth/context/AuthContext';
import ContactForm from '../../ContactManagement/components/ContactForm';
import type { Contact } from '../../ContactManagement/types';
import { INVITE_ROLES, InviteRoleKey } from '../inviteRoles';
import { X } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';

interface InviteMemberModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * A modal for inviting organization members by role,
 * reusing existing contacts for Staff or Ironworker.
 */
export default function InviteMemberModal({ onClose, onSuccess }: InviteMemberModalProps) {
  const { auth } = useContext(AuthContext);
  // Navigation no longer used; using inline contact form instead
  // const navigate = useNavigate();

  // Role selection state
  const [roleKey, setRoleKey] = useState<InviteRoleKey>('foreman');
  const role = INVITE_ROLES[roleKey];

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Selected contact for invitation
  const [selected, setSelected] = useState<Contact | null>(null);

  // Fallback fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // New contact modal state
  const [showContactForm, setShowContactForm] = useState(false);

  // Fetch contacts when searchTerm and role.contactType change
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await axios.get('/api/myteconstruction/contacts', {
          headers: { Authorization: `Bearer ${auth.token}` },
          params: {
            contactType: role.contactType,
            search: searchTerm,
            limit: 10,
          },
        });
        setResults(resp.data.data || []);
      } catch (e) {
        setError('Failed to search contacts');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, role.contactType, auth.token]);

  const handleSelect = (c: Contact) => {
    setSelected(c);
    setName(c.name);
    setEmail(c.email);
    setResults([]);
    setSearchTerm('');
  };

  // Open inline contact creation form
  const handleAddContact = () => {
    setShowContactForm(true);
  };

  const handleInvite = async () => {
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required');
      return;
    }
    try {
      // Build payload, include contactId if selected
      const payload: Record<string, any> = { role: role.displayName, name, email };
      if (selected && selected._id) payload.contactId = selected._id;
      await axios.post(
        '/api/myteconstruction/invitations',
        payload,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      setSuccess('Invitation email sent successfully');
      setError('');
      // Delay closing to show confirmation
      setTimeout(() => onSuccess(), 2000);
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any;
      setError(err.response?.data?.error || 'Failed to send invite');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        {showContactForm ? (
          <ContactForm
            contact={null}
            onClose={() => setShowContactForm(false)}
            /** Prefill kind/type based on invite role */
            initialContactKind={role.contactType === 'Staff' ? 'Business' : 'Individual'}
            initialContactType={role.contactType}
            onSubmit={(c: Contact) => {
              // Populate invite fields and record contactId
              setName(c.name || '');
              setEmail(c.email || '');
              setSelected(c);
              setShowContactForm(false);
            }}
          />
        ) : (
          <>
            <h3 className="text-lg font-medium mb-4 text-white">Invite {role.displayName}</h3>

            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Role</label>
              <select
                value={roleKey}
                onChange={e => setRoleKey(e.target.value as InviteRoleKey)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
              >
                {Object.values(INVITE_ROLES).map(r => (
                  <option key={r.key} value={r.key}>{r.displayName}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">
                Search Existing {role.displayName}
              </label>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={`Type to search ${role.displayName}`}
              />
              {loading && <div className="text-gray-400 text-sm mt-1">Searching...</div>}
              {!loading && results.length > 0 && (
                <ul className="bg-gray-700 mt-1 max-h-48 overflow-y-auto rounded-md">
                  {results.map(c => (
                    <li
                      key={c._id}
                      className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-sm text-white"
                      onClick={() => handleSelect(c)}
                    >
                      {c.name} &lt;{c.email}&gt;
                    </li>
                  ))}
                </ul>
              )}
              {!loading && searchTerm.length >= 2 && results.length === 0 && (
                <div className="mt-2 text-sm text-gray-300">
                  No contacts found.{' '}
                  <button onClick={handleAddContact} className="text-blue-400 underline">
                    Add {role.displayName}
                  </button>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Name</label>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Full name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Email</label>
              <input
                type="email"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email address"
              />
            </div>

            {error && <div className="text-red-500 mb-2">{error}</div>}
            {success && <div className="text-green-500 mb-2">{success}</div>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded-md text-white">
                Cancel
              </button>
              <button onClick={handleInvite} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-white">
                Invite {role.displayName}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}